import { useCallback, useRef } from 'react';

/**
 * Simple in-memory cache for agent favicons.
 * Follows the codebase patterns using React refs and callbacks.
 */
export const useAgentFaviconCache = (): {
	get: (agentName: string) => string | null;
	set: (agentName: string, faviconUrl: string) => void;
	clear: () => void;
	delete: (agentName: string) => void;
} => {
	const cacheRef = useRef<Map<string, string>>(new Map());

	/**
	 * Get cached favicon URL for an agent by name
	 */
	const get = useCallback((agentName: string): string | null => {
		return cacheRef.current.get(agentName) || null;
	}, []);

	/**
	 * Set cached favicon URL for an agent by name
	 */
	const set = useCallback((agentName: string, faviconUrl: string): void => {
		cacheRef.current.set(agentName, faviconUrl);
	}, []);

	/**
	 * Clear all cached favicons
	 */
	const clear = useCallback((): void => {
		cacheRef.current.clear();
	}, []);

	/**
	 * Remove a specific agent's favicon from cache by name
	 */
	const deleteAgent = useCallback((agentName: string): void => {
		cacheRef.current.delete(agentName);
	}, []);

	return {
		get,
		set,
		clear,
		delete: deleteAgent,
	};
};
